import { useState, useEffect, useRef, useCallback } from "react";
import { BLACKWAVE_CC_MAP, BLACKWAVE_CC_TO_NAME } from "../../Data/products/blackwave/panelCcMap.js";

// Pannello interattivo BLACKWAVE: consuma il manifest esportato da PanelDesigner
// (sfondo SVG + asset knob/switch + posizioni normalizzate) e lo rende interattivo
// via MIDI CC. I knob ruotano al drag e inviano il CC; gli switch fanno toggle. Il
// CC in ingresso dal device (es. al load di un preset) riposiziona i controlli.

// SysEx STATE DUMP request (vedi firmware midi_handlers.cpp, CMD 0x04): chiede al
// device di emettere lo stato completo (tutti i CC pot+switch + il Program Change
// del preset attivo). Serve a sincronizzare il pannello all'apertura: il CC/PC out
// del firmware manda solo i CAMBI, quindi senza dump la UI non conosce lo stato
// iniziale. La risposta arriva come normali CC/PC, già gestiti dai listener sotto.
const STATE_DUMP_REQUEST = [0xf0, 0x00, 0x02, 0x0d, 0x04, 0xf7];

// SysEx LED STREAM (firmware CMD 0x05, solo device -> editor): livelli 0..127 dei 4
// LED "brightness" animati da envelope/LFO, per replicarli in tempo reale. Payload
// F0 00 02 0D 05 <overload> <eg1/VCA> <eg2/VCF> <rate> F7. Ordine → nome control.
const SYSEX_CMD_LED_STREAM = 0x05;
const LED_STREAM_NAMES = ["LED_OVERLOAD", "LEG_EG1", "LEG_EG2", "LED_RATE"];

const stripXml = svg => (svg || "").replace(/<\?xml[^>]*\?>/i, "").trim();

// Prepara lo sfondo: toglie il prologo <?xml?> e le dimensioni mm esplicite dal <svg>
// root (il box lo dimensioniamo noi in px). Il viewBox resta e l'aspect è preservato
// (box in px = mm·scale), quindi lo sfondo riempie 1:1 senza distorsione.
const prepBackground = svg =>
  stripXml(svg)
    .replace(/(<svg\b[^>]*?)\s+width="[^"]*"/i, "$1")
    .replace(/(<svg\b[^>]*?)\s+height="[^"]*"/i, "$1");

// La graphicUrl è una data URL SVG. Renderla come <img> con dimensioni mm +
// preserveAspectRatio="none" non la centra bene nel browser. Come per lo sfondo,
// la si INLINE (viewBox + CSS 100%): decodifico la data URL e tolgo width/height
// dal root, così l'SVG riempie il box e il disegno resta centrato nel viewBox.
//
// Le classi Adobe (.st0, .st1…) sono generiche e uguali tra asset diversi, ma con
// palette diverse (es. CUTOFF ha .st0=#ccc, gli altri .st0=none). Injectate inline,
// i <style> non sono scoped → collidono nel CSS globale e vince l'ultimo nel DOM
// (CUTOFF appariva invertito). Si namespacano con l'assetId: prefisso stabile per
// asset, palette coerente al suo interno, asset diversi separati.
const inlineGraphic = (url, key) => {
  const svg = prepBackground(decodeURIComponent((url || "").replace(/^data:image\/svg\+xml[^,]*,/i, "")));
  const ns = `bwg-${key}`;
  return svg.replace(/\.st(\d+)\b/g, `.${ns}_st$1`).replace(/class="st(\d+)"/g, `class="${ns}_st$1"`);
};

// Angolo del knob per un valore 0..127. Replica PanelDesigner: la freccia punta a
// targetAngle = (value-centro)·sweep; la grafica ha un orientamento di partenza →
// si somma graphicSvgRotDeg e si sottrae photoAngleDeg. Sweep = rotationDeg.
function knobAngle(value, ctrl, asset) {
  const sweep = ctrl.rotationDeg || 300;
  const target = (value / 127 - 0.5) * sweep;
  return (asset.graphicSvgRotDeg || 0) + target - (asset.photoAngleDeg || 0);
}

// LED_PRESET_1..6 → numero preset (1..6). Gli altri LED (OVERLOAD, RATE, EG…) → null.
const PRESET_LED_RE = /^LED_PRESET_([1-6])$/;
const presetNumFromName = name => {
  const m = PRESET_LED_RE.exec(name || "");
  return m ? Number(m[1]) : null;
};

// valore di default di un control. Override espliciti per alcuni knob; altrimenti i
// bipolari (detent centrale) a 64, gli altri a 0.
const DEFAULT_OVERRIDES = {
  VCO_FREQ: 64, // frequency a metà corsa
  VCO_BEAT: 64, // beat a metà corsa
  MOD_SOURCE: 64, // mod source a metà corsa
  MOD_RATE: 64, // rate a metà corsa
  VOLUME_VCO1: 127, // volume VCO1 al massimo
  EG1_SUSTAIN: 127, // sustain EG1 al massimo
  EG2_SUSTAIN: 127, // sustain EG2 al massimo
  CUTOFF: 127, // cutoff al massimo
  VOLUME: 127, // volume master (senza CC) al massimo
};
const defaultValueFor = name =>
  name in DEFAULT_OVERRIDES ? DEFAULT_OVERRIDES[name] : BLACKWAVE_CC_MAP[name]?.bipolar ? 64 : 0;

export default function BlackwavePanel({ manifestUrl, output, input, channel, devMode, onLoadedChange, onValuesChange }) {
  const [manifest, setManifest] = useState(null);
  const [error, setError] = useState(null);
  const [values, setValues] = useState({}); // controlId -> 0..127
  const [ledLevels, setLedLevels] = useState({}); // controlName -> 0..127 (LED animati: overload/EG1/EG2/rate)
  const loadedRef = useRef(false); // true dopo aver ricevuto il dump dei parametri dall'hardware
  const loadTimerRef = useRef(null); // debounce di fine-raffica dello STATE DUMP
  const [activePreset, setActivePreset] = useState(1); // preset acceso (1..6); default 1 come BLACKWAVE hw (changeCurrentPreset(0) al boot)
  const [hintId, setHintId] = useState(null); // control il cui tooltip è visibile (uno per volta)
  const [panelWidthPx, setPanelWidthPx] = useState(0); // larghezza reale del contenitore
  const dragRef = useRef(null);
  const wrapRef = useRef(null);
  const hintHideTimer = useRef(null); // nasconde il tooltip ~1s dopo la fine della modifica
  const hintHoverTimer = useRef(null); // mostra il tooltip dopo 1s di hover fermo

  // il tooltip appare solo se: premo/muovo un controllo, oppure hover fermo >1s; poi
  // sparisce ~1s dopo la fine dell'interazione.
  const showHint = useCallback(id => {
    if (hintHideTimer.current) {
      clearTimeout(hintHideTimer.current);
      hintHideTimer.current = null;
    }
    setHintId(id);
  }, []);
  const hideHintSoon = useCallback(() => {
    if (hintHideTimer.current) clearTimeout(hintHideTimer.current);
    hintHideTimer.current = setTimeout(() => setHintId(null), 1000);
  }, []);
  const onHintEnter = useCallback(
    id => {
      if (hintHoverTimer.current) clearTimeout(hintHoverTimer.current);
      hintHoverTimer.current = setTimeout(() => showHint(id), 1000);
    },
    [showHint]
  );
  const onHintLeave = useCallback(() => {
    if (hintHoverTimer.current) {
      clearTimeout(hintHoverTimer.current);
      hintHoverTimer.current = null;
    }
    hideHintSoon();
  }, [hideHintSoon]);
  useEffect(
    () => () => {
      if (hintHideTimer.current) clearTimeout(hintHideTimer.current);
      if (hintHoverTimer.current) clearTimeout(hintHoverTimer.current);
    },
    []
  );

  // misura la larghezza del pannello → scala uniforme px/mm (come lo zoom di Konva)
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      for (const e of entries) setPanelWidthPx(e.contentRect.width);
    });
    ro.observe(el);
    setPanelWidthPx(el.clientWidth);
    return () => ro.disconnect();
  }, [manifest]);

  // carica il manifest
  useEffect(() => {
    let alive = true;
    fetch(manifestUrl)
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(m => {
        if (!alive) return;
        setManifest(m);
        const init = {};
        for (const c of m.controls) {
          init[c.id] = defaultValueFor(c.name); // bipolari centrati (64), gli altri a zero
        }
        setValues(init);
      })
      .catch(e => alive && setError(e.message));
    return () => {
      alive = false;
    };
  }, [manifestUrl]);

  const sendCc = useCallback(
    (name, value) => {
      const map = BLACKWAVE_CC_MAP[name];
      if (!map) return; // control senza CC (VOLUME master, FN)
      // switch a polarita' invertita (VCO2_SYNC/WAVE): il valore interno e' lo stato
      // logico mostrato, sul CC va la polarita' opposta (vedi nota in panelCcMap).
      const out = map.inverted ? 127 - (value & 0x7f) : value & 0x7f;
      if (devMode) {
        console.log(`DEV MODE — CC ${map.cc} = ${out} (${name})`);
        return;
      }
      if (output) output.send([0xb0 | ((channel - 1) & 0x0f), map.cc, out & 0x7f]);
    },
    [output, channel, devMode]
  );

  // Click su un LED preset (1..6): manda un Program Change (program = preset-1, come
  // changeCurrentPreset 0-based nel firmware) e accende il LED. Uno solo per volta.
  // program 0 = LED_PRESET_1 = slot LIVE (stato pannello); 1..5 = preset veri. Il
  // firmware carica il preset e rimanda i CC dei param cambiati (aggiorna i knob).
  const selectPreset = useCallback(
    preset => {
      setActivePreset(preset);
      if (devMode) {
        console.log(`DEV MODE — Program Change ${preset - 1} (preset ${preset})`);
        return;
      }
      if (output) output.send([0xc0 | ((channel - 1) & 0x0f), (preset - 1) & 0x7f]);
    },
    [output, channel, devMode]
  );

  // CC in ingresso dal device → sposta il control corrispondente. Program Change in
  // → accende il LED del preset attivo (il firmware lo manda quando il preset cambia
  // da touch/SysEx: program 0..5 → activePreset 1..6).
  useEffect(() => {
    if (!input || !manifest) return;
    // Il device risponde allo STATE DUMP con una raffica di CC + un PC finale. Non
    // c'e' un marcatore di fine univoco, quindi: il PC (ultimo del dump) chiude subito
    // il load; in mancanza, il PRIMO messaggio arma un timer FISSO (non riarmabile) che
    // chiude il load quando la raffica dovrebbe essere finita. Il timer non si riavvia
    // a ogni CC, cosi' un eventuale dither continuo di un pot non tiene l'editor
    // bloccato per sempre.
    const finishLoad = () => {
      if (loadedRef.current) return;
      loadedRef.current = true;
      if (loadTimerRef.current) {
        clearTimeout(loadTimerRef.current);
        loadTimerRef.current = null;
      }
      if (onLoadedChange) onLoadedChange(true);
    };
    const armLoad = () => {
      if (loadedRef.current || loadTimerRef.current) return;
      loadTimerRef.current = setTimeout(finishLoad, 600);
    };
    const onMsg = event => {
      const d = event.data;
      // SysEx LED STREAM (0x05): livelli dei LED animati → aggiorna la brightness
      if (d[0] === 0xf0) {
        if (d[1] === 0x00 && d[2] === 0x02 && d[3] === 0x0d && d[4] === SYSEX_CMD_LED_STREAM && d.length >= 9) {
          setLedLevels({
            [LED_STREAM_NAMES[0]]: d[5],
            [LED_STREAM_NAMES[1]]: d[6],
            [LED_STREAM_NAMES[2]]: d[7],
            [LED_STREAM_NAMES[3]]: d[8],
          });
        }
        return; // altri SysEx (settings READ RESPONSE) li gestisce BlackwaveEditor
      }
      const status = d[0] & 0xf0;
      if (status === 0xc0) {
        // Program Change: d[1] = program (0-based). LED = program+1
        const preset = (d[1] & 0x7f) + 1;
        if (preset >= 1 && preset <= 6) setActivePreset(preset);
        finishLoad(); // il PC chiude il dump
        return;
      }
      if (status !== 0xb0) return; // altrimenti solo Control Change
      const name = BLACKWAVE_CC_TO_NAME[d[1]];
      if (!name) return;
      const ctrl = manifest.controls.find(c => c.name === name);
      if (!ctrl) return;
      // switch a polarita' invertita: riporta il CC in ingresso allo stato logico
      const map = BLACKWAVE_CC_MAP[name];
      const val = map && map.inverted ? 127 - d[2] : d[2];
      setValues(v => ({ ...v, [ctrl.id]: val }));
      armLoad();
    };
    input.addEventListener("midimessage", onMsg);
    return () => {
      input.removeEventListener("midimessage", onMsg);
      if (loadTimerRef.current) clearTimeout(loadTimerRef.current);
    };
  }, [input, manifest, onLoadedChange]);

  // Sync iniziale: appena device (output+input) e manifest sono pronti, chiedi un
  // STATE DUMP → il device emette tutti i CC + il PC del preset attivo, catturati dal
  // listener sopra. Una volta per connessione (guardia su output+input).
  const dumpRetryRef = useRef(null);
  useEffect(() => {
    if (!output || !input || !manifest || devMode) {
      loadedRef.current = false; // torna "non sincronizzato" finche' non arriva un nuovo dump
      if (loadTimerRef.current) {
        clearTimeout(loadTimerRef.current);
        loadTimerRef.current = null;
      }
      if (dumpRetryRef.current) {
        clearInterval(dumpRetryRef.current);
        dumpRetryRef.current = null;
      }
      if (onLoadedChange) onLoadedChange(false);
      return;
    }
    // Chiede lo STATE DUMP e ritenta finche' non arriva risposta: il device appena
    // enumerato puo' non essere pronto al primo invio. Si ferma appena "loaded" o dopo
    // un tot di tentativi. NB: NON recupera i messaggi persi per overflow del buffer TX
    // del firmware (fix firmware CFG_TUD_MIDI_TX_BUFSIZE) -- serve solo alla readiness.
    let attempts = 0;
    const request = () => {
      if (loadedRef.current || attempts >= 8) {
        if (dumpRetryRef.current) {
          clearInterval(dumpRetryRef.current);
          dumpRetryRef.current = null;
        }
        return;
      }
      attempts += 1;
      output.send(STATE_DUMP_REQUEST);
    };
    request();
    dumpRetryRef.current = setInterval(request, 1000);
    return () => {
      if (dumpRetryRef.current) {
        clearInterval(dumpRetryRef.current);
        dumpRetryRef.current = null;
      }
    };
  }, [output, input, manifest, devMode, onLoadedChange]);

  // Espone i valori correnti (nome-control -> 0..127) al genitore, per gli
  // specchietti (scope) affianco al pannello. Scatta a ogni cambio di values:
  // dump iniziale, edit dall'editor, CC in arrivo dall'hardware.
  useEffect(() => {
    if (!onValuesChange || !manifest) return;
    const byName = {};
    for (const c of manifest.controls) byName[c.name] = values[c.id] ?? 0;
    onValuesChange(byName);
  }, [values, manifest, onValuesChange]);

  const setControlValue = useCallback(
    (ctrl, value) => {
      const v = Math.max(0, Math.min(127, Math.round(value)));
      setValues(prev => ({ ...prev, [ctrl.id]: v }));
      sendCc(ctrl.name, v);
    },
    [sendCc]
  );

  // drag verticale sul knob → varia il valore; click secco (senza drag) → torna al default
  const KNOB_DRAG_THRESHOLD = 3; // px sotto i quali è un click, non un drag
  const onKnobPointerDown = (e, ctrl) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { id: ctrl.id, startY: e.clientY, startVal: values[ctrl.id] ?? 0, moved: false };
    showHint(ctrl.id);
  };
  const onKnobPointerMove = (e, ctrl) => {
    const d = dragRef.current;
    if (!d || d.id !== ctrl.id) return;
    const dy = d.startY - e.clientY; // su = aumenta
    if (!d.moved && Math.abs(dy) < KNOB_DRAG_THRESHOLD) return; // ignora il jitter del click
    d.moved = true;
    setControlValue(ctrl, d.startVal + dy); // 1px ≈ 1 step
    showHint(ctrl.id);
  };
  const onKnobPointerUp = (e, ctrl) => {
    const d = dragRef.current;
    if (d?.id === ctrl.id) {
      if (!d.moved) setControlValue(ctrl, defaultValueFor(ctrl.name)); // click → reset al default
      dragRef.current = null;
    }
    hideHintSoon();
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const onSwitchClick = ctrl => {
    const cur = values[ctrl.id] ?? 0;
    setControlValue(ctrl, cur >= 64 ? 0 : 127);
    showHint(ctrl.id);
    hideHintSoon();
  };

  if (error) return <div className="blackwave-editor__panel-placeholder">Panel load error: {error}</div>;
  if (!manifest) return <div className="blackwave-editor__panel-placeholder">Loading panel…</div>;

  const { panel, assets, controls } = manifest;
  // Scala uniforme px/mm (come lo zoom di Konva): TUTTO in px = mm·scale, stessa scala
  // su entrambi gli assi → impossibile stretch o offset. L'altezza segue la larghezza.
  const scale = panel.widthMm > 0 ? panelWidthPx / panel.widthMm : 0;
  const heightPx = panel.heightMm * scale;
  const switchMm = 6; // dimensione di default degli switch (il manifest non porta mm dei button)
  // debug: ?debug=1 (o dev mode) disegna un puntino rosso al centro ESATTO calcolato di
  // ogni controllo → dice se l'offset è nei dati (puntino spostato) o nel rendering.
  const debug = devMode || (typeof window !== "undefined" && /[?&]debug=1\b/.test(window.location.search));

  return (
    <div
      className={`bw-panel${debug ? " bw-debug" : ""}`}
      ref={wrapRef}
      style={heightPx ? { height: `${heightPx}px` } : undefined}
    >
      <div className="bw-panel__bg" dangerouslySetInnerHTML={{ __html: prepBackground(panel.backgroundSvg) }} />
      {scale > 0 &&
        controls.map(ctrl => {
          const asset = assets[ctrl.assetId] || {};
          const left = ctrl.cxMm * scale;
          const top = ctrl.cyMm * scale;
          const value = values[ctrl.id] ?? 0;
          const map = BLACKWAVE_CC_MAP[ctrl.name];
          const hasCc = !!map;
          // tooltip live: CC + valore corrente (mostrato su hover e durante la modifica)
          const hint = hasCc ? `CC ${map.cc} · ${value}` : "No CC";

          if (ctrl.kind === "knob") {
            // box reale in mm (graphicBoxWmm/Hmm già con pad+scala) → px con la stessa scala
            const wPx = (asset.graphicBoxWmm || 12) * scale;
            const hPx = (asset.graphicBoxHmm || 12) * scale;
            const angle = knobAngle(value, ctrl, asset);
            const gfxStyle = { transform: `rotate(${angle.toFixed(2)}deg)` };
            const drag = hasCc
              ? {
                  onPointerDown: e => onKnobPointerDown(e, ctrl),
                  onPointerMove: e => onKnobPointerMove(e, ctrl),
                  onPointerUp: e => onKnobPointerUp(e, ctrl),
                  onPointerEnter: () => onHintEnter(ctrl.id),
                  onPointerLeave: onHintLeave,
                }
              : {};
            return (
              <div
                key={ctrl.id}
                className={`bw-knob${hasCc ? "" : " bw-knob--locked"}`}
                style={{ left: `${left}px`, top: `${top}px`, width: `${wPx}px`, height: `${hPx}px` }}
                {...drag}
              >
                {hasCc && <span className={`bw-hint${hintId === ctrl.id ? " is-visible" : ""}`}>{hint}</span>}
                {asset.graphicUrl ? (
                  <div
                    className="bw-knob__gfx"
                    style={gfxStyle}
                    dangerouslySetInnerHTML={{ __html: inlineGraphic(asset.graphicUrl, ctrl.assetId) }}
                  />
                ) : (
                  <div className="bw-knob__gfx" style={gfxStyle}>
                    {asset.src ? <img src={asset.src} alt="" draggable={false} /> : null}
                  </div>
                )}
              </div>
            );
          }

          if (ctrl.kind === "led") {
            const wPx = (asset.graphicBoxWmm || 4) * scale;
            const hPx = (asset.graphicBoxHmm || 4) * scale;
            const gfx = asset.graphicUrl ? (
              <span
                className="bw-led__gfx"
                dangerouslySetInnerHTML={{ __html: inlineGraphic(asset.graphicUrl, ctrl.assetId) }}
              />
            ) : null;
            const preset = presetNumFromName(ctrl.name);
            // LED preset: cliccabile, con area di hit +20px oltre il LED, si accende (uno solo)
            if (preset != null) {
              const hitPx = 32; // px totali di area cliccabile in più (16 per lato)
              const lit = activePreset === preset;
              return (
                <button
                  key={ctrl.id}
                  type="button"
                  className={`bw-led bw-led--preset${lit ? " bw-led--on" : ""}`}
                  style={{ left: `${left}px`, top: `${top}px`, width: `${wPx + hitPx}px`, height: `${hPx + hitPx}px` }}
                  onClick={() => {
                    selectPreset(preset);
                    showHint(ctrl.id);
                    hideHintSoon();
                  }}
                >
                  <span className={`bw-hint${hintId === ctrl.id ? " is-visible" : ""}`}>{`Program Change ${preset - 1}`}</span>
                  <span className="bw-led__gfx-wrap" style={{ width: `${wPx}px`, height: `${hPx}px` }}>
                    {gfx}
                  </span>
                </button>
              );
            }
            // altri LED (OVERLOAD, RATE, EG1/LEG_EG1, EG2/LEG_EG2): non cliccabili,
            // ma con brightness in tempo reale dallo stream SysEx (ledLevels 0..127).
            // norm 0 = spento (base), 1 = pieno (stesso look del preset acceso).
            const norm = Math.max(0, Math.min(1, (ledLevels[ctrl.name] ?? 0) / 127));
            const litStyle =
              norm > 0.01
                ? {
                    filter: `brightness(${(1 + 2.2 * norm).toFixed(2)}) drop-shadow(0 0 ${(4 * norm).toFixed(
                      1
                    )}px rgb(255 40 36 / ${(0.85 * norm).toFixed(2)}))`,
                  }
                : undefined;
            return (
              <div
                key={ctrl.id}
                className="bw-led"
                style={{ left: `${left}px`, top: `${top}px`, width: `${wPx}px`, height: `${hPx}px` }}
              >
                {asset.graphicUrl ? (
                  <span
                    className="bw-led__gfx"
                    style={litStyle}
                    dangerouslySetInnerHTML={{ __html: inlineGraphic(asset.graphicUrl, ctrl.assetId) }}
                  />
                ) : null}
              </div>
            );
          }

          const on = value >= 64;
          // dimensione dal box reale del graphic (come i knob); switchMm è solo fallback
          // se l'asset non porta le mm. Gli switch NON ruotano.
          const wPx = (asset.graphicBoxWmm || switchMm) * scale;
          const hPx = (asset.graphicBoxHmm || switchMm) * scale;
          return (
            <button
              key={ctrl.id}
              type="button"
              className={`bw-switch${on ? " bw-switch--on" : ""}${hasCc ? "" : " bw-switch--locked"}`}
              style={{ left: `${left}px`, top: `${top}px`, width: `${wPx}px`, height: `${hPx}px` }}
              disabled={!hasCc}
              onClick={hasCc ? () => onSwitchClick(ctrl) : undefined}
              onPointerEnter={hasCc ? () => onHintEnter(ctrl.id) : undefined}
              onPointerLeave={hasCc ? onHintLeave : undefined}
            >
              {hasCc && <span className={`bw-hint${hintId === ctrl.id ? " is-visible" : ""}`}>{hint}</span>}
              {asset.graphicUrl ? (
                <span
                  className="bw-switch__gfx"
                  dangerouslySetInnerHTML={{ __html: inlineGraphic(asset.graphicUrl, ctrl.assetId) }}
                />
              ) : asset.src ? (
                <img src={asset.src} alt="" draggable={false} />
              ) : (
                <span className="bw-switch__dot" />
              )}
            </button>
          );
        })}
      {debug &&
        scale > 0 &&
        controls.map(ctrl => (
          <div
            key={`mk-${ctrl.id}`}
            className="bw-mark"
            style={{ left: `${ctrl.cxMm * scale}px`, top: `${ctrl.cyMm * scale}px` }}
          />
        ))}
    </div>
  );
}
